import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import SEO from '../components/SEO';
import { SITE_NAME, SITE_URL } from '../constants';

type NewsPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  featuredImage: string;
  author?: { name?: string; email?: string; avatar?: string } | null;
  seo?: { metaTitle?: string; metaDescription?: string; canonicalUrl?: string; keywords?: string[] } | null;
  readingTime: number | null;
  publishedAt: string | null;
  updatedAt?: string | null;
};

const isRemoteUrl = (value?: string | null) => /^https?:\/\//i.test(value || '');
const buildImageUrl = (src?: string | null, width = 1600) => {
  if (!src) return '';
  if (isRemoteUrl(src)) return src;
  return `/image?src=${encodeURIComponent(src)}&w=${width}`;
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const NewsPostPage: React.FC = () => {
  const { slug } = useParams();
  const bootstrap =
    typeof window !== 'undefined'
      ? ((window as typeof window & { __NEWS_POST_BOOTSTRAP__?: NewsPost }).__NEWS_POST_BOOTSTRAP__ as NewsPost | undefined)
      : ((globalThis as typeof globalThis & { __SSR_NEWS_POST_BOOTSTRAP__?: NewsPost }).__SSR_NEWS_POST_BOOTSTRAP__ as NewsPost | undefined);
  const [post, setPost] = useState<NewsPost | null>(bootstrap && bootstrap.slug === slug ? bootstrap : null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!(bootstrap && bootstrap.slug === slug));

  useEffect(() => {
    let isMounted = true;
    const loadPost = async () => {
      if (!slug) {
        setError('News item not found.');
        setIsLoading(false);
        return;
      }
      if (bootstrap && bootstrap.slug === slug) {
        delete (window as typeof window & { __NEWS_POST_BOOTSTRAP__?: NewsPost }).__NEWS_POST_BOOTSTRAP__;
        setPost(bootstrap);
        setError(null);
        setIsLoading(false);
        return;
      }
      try {
        const response = await fetch(`/api/news/${slug}`);
        if (!response.ok) throw new Error('Request failed');
        const payload = await response.json();
        if (isMounted) setPost(payload);
      } catch {
        if (isMounted) setError('Unable to load this news item.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadPost();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  const publishedLabel = useMemo(() => post?.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : null, [post?.publishedAt]);
  const contentHtml = useMemo(() => {
    const raw = post?.content || '';
    return /<\/?[a-z][\s\S]*>/i.test(raw) ? raw : escapeHtml(raw).replace(/\n/g, '<br />');
  }, [post?.content]);
  const canonical = post?.seo?.canonicalUrl || (post?.slug ? `${SITE_URL}/news/${post.slug}` : undefined);
  const ogImage = post?.featuredImage ? (isRemoteUrl(post.featuredImage) ? post.featuredImage : `${SITE_URL}${post.featuredImage}`) : `${SITE_URL}/AviaGov.svg`;
  const authorName = post?.author?.name || 'AviaGov Editorial Team';
  const featuredImage = buildImageUrl(post?.featuredImage, 1600);
  const schema = useMemo(() => post && canonical ? [{
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: post.title,
    description: post.seo?.metaDescription || post.excerpt,
    articleSection: post.category || 'Company news',
    mainEntityOfPage: canonical,
    url: canonical,
    image: ogImage,
    keywords: post.seo?.keywords || post.tags,
    author: { '@type': 'Person', name: authorName },
    publisher: { '@type': 'Organization', name: SITE_NAME, logo: { '@type': 'ImageObject', url: `${SITE_URL}/AviaGov.svg` } },
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
  }] : undefined, [authorName, canonical, ogImage, post]);

  if (isLoading) return <div className="animate-fade-in-up"><section className="py-24 px-4 max-w-4xl mx-auto"><p className="text-slate-400">Loading news...</p></section></div>;
  if (error || !post) return <div className="animate-fade-in-up"><section className="py-24 px-4 max-w-4xl mx-auto text-center"><p className="text-red-400 mb-6">{error || 'News item not found.'}</p><Link to="/news" className="text-accent font-bold hover:underline">Back to news</Link></section></div>;

  return (
    <div className="animate-fade-in-up">
      <SEO title={post.seo?.metaTitle || post.title} description={post.seo?.metaDescription || post.excerpt} canonical={canonical} keywords={post.seo?.keywords || post.tags} ogType="article" ogImage={ogImage} schema={schema} />
      <section className="py-24 px-4 max-w-4xl mx-auto">
        <Link to="/news" className="text-accent text-xs font-bold uppercase tracking-widest">&larr; Back to news</Link>
        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-400">
          <span className="text-accent font-bold">{post.category || 'News'}</span>
          {publishedLabel ? <span>{publishedLabel}</span> : null}
          {post.readingTime ? <span>{post.readingTime} min read</span> : null}
          <span>Non-regulatory</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold font-heading mt-6 mb-6">{post.title}</h1>
        <p className="text-lg text-slate-400 mb-10">{post.excerpt}</p>
        {featuredImage ? <img src={featuredImage} alt={post.title} className="w-full rounded-[2rem] border border-white/10 mb-12" width={1600} height={900} loading="eager" decoding="async" /> : null}
        <div className="blog-content text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: contentHtml }} />
        {post.tags?.length ? <div className="mt-12 flex flex-wrap gap-3">{post.tags.map((tag) => <span key={tag} className="text-xs uppercase tracking-widest text-slate-400 border border-white/10 px-3 py-2 rounded-full">{tag}</span>)}</div> : null}
        <div className="mt-12 p-6 border border-white/10 rounded-2xl"><p className="text-sm text-slate-400">Published by</p><p className="text-white font-bold">{authorName}</p></div>
      </section>
    </div>
  );
};

export default NewsPostPage;
